import { cn } from '@goaltree/ui/lib/utils'
import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { useCoachPersona } from '@/components/ask-coach'
import { CoachLauncherFace } from '@/components/coach-avatar'
import { bareName } from '@/components/coaching/coach-utils'
import {
  HumanCoachAvatar,
  openCoachNotes,
  useCoachInbox,
  useCoachNotesOpen,
} from '@/components/coaching/my-coach-notes'
import { IconTip } from '@/components/icon-tip'
import { useCoachCall } from '@/lib/coach-call-store'
import { useIsCoaching } from '@/lib/coaching-mode'

const FACE = 32

const openAiCoach = () => window.dispatchEvent(new CustomEvent('open-coach'))

// The accent ring like the sidebar's coach card; full strength while its sheet is open.
const ring = (open: boolean) => (open ? 'ring-2 ring-primary' : 'ring-2 ring-primary/60')

// A bare face in the header's 32px icon slot, with a 40px hit area.
function FaceButton({
  label,
  tooltip,
  onClick,
  children,
  className,
}: {
  label: string
  tooltip: string
  onClick: () => void
  children: ReactNode
  className?: string
}) {
  return (
    <IconTip label={tooltip}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className={cn(
          '-m-1 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full transition-transform outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-95',
          className,
        )}
      >
        {children}
      </button>
    </IconTip>
  )
}

// Header coaches: the AI coach's face opens the chat; a human coach's opens their notes.
function CoachHeaderButtonInner(_: { transparent?: boolean }) {
  const { t } = useTranslation()
  const { name } = useCoachPersona()
  const inbox = useCoachInbox()
  const aiOpen = useCoachCall((s) => s.sheet.open)
  const notesOpen = useCoachNotesOpen((s) => s.open)
  const aiLabel = t('coach.coachLabel', { name })
  const human = inbox && t('coach.coachLabel', { name: bareName(inbox.coach.name) })
  return (
    <span className="flex items-center">
      <FaceButton label={aiLabel} tooltip={aiLabel} onClick={openAiCoach}>
        <span className={cn('inline-flex rounded-full transition-shadow', ring(aiOpen))}>
          <CoachLauncherFace size={FACE} />
        </span>
      </FaceButton>
      {inbox && human && (
        // Overlaps the AI face like a stack; the background ring keeps the edge readable.
        <FaceButton
          label={inbox.unread ? `${human} · ${t('coaching.coaches.newNote')}` : human}
          tooltip={human}
          onClick={openCoachNotes}
          className="relative -ml-4"
        >
          <HumanCoachAvatar
            image={inbox.coach.image}
            name={inbox.coach.name}
            size={FACE}
            dot={inbox.unread}
            ring={cn(
              'transition-shadow',
              notesOpen ? ring(true) : 'shadow-[0_0_0_2px_var(--background)]',
            )}
          />
        </FaceButton>
      )}
    </span>
  )
}

// The AI coach belongs to the personal side: hidden in coaching mode.
export function CoachHeaderButton(props: ComponentProps<typeof CoachHeaderButtonInner>) {
  return useIsCoaching() ? null : <CoachHeaderButtonInner {...props} />
}
